import { NameDto } from '../../Common/Types'

export interface ScreeningResultsDto {
  id: string
  caseId: string
  clientId: string
  ongoingScreeningEnabled: boolean
  latestRequiredReviewDate: string | null
  records: ScreeningResultDto[]
  status: NameDto
  systemCaseId: string
  createdAt: string
  type: {
    id: number
    name: string
  }
}

export interface ScreeningResultDto {
  id: string
  isMatch: boolean
  profile?: WorldCheckScreeningProfile | null
  reason: {
    id: number
    name: string
  }
  referenceId: string
  remark: string | null
  result: Result | null
  resultId: string
  riskLevel: {
    id: number
    name: string
  }
  status: {
    id: number
    name: string
  }
  selected?: boolean
}

interface Result {
  resultId: string
  referenceId: string
  matchStrength: string
  matchedTerm: string
  submittedTerm: string
  matchedNameType: string
  secondaryFieldResults: SecondaryFieldResult[]
  sources: string[]
  categories: string[]
  creationDate: string
  modificationDate: string
  resolution: {
    valueKind: string
  } | null
  primaryName: string
  gender: string // 'gender' can be any string value based on JSON
  birthAddress: Address | null // added Address interface
  placeOfBirth: string | null
  dateOfBirth: string | null
  nationality: string
  location: string
  resultReview: {
    reviewRequired: boolean
    reviewRequiredDate: string
    reviewRemark: string | null
    reviewDate: string | null
  }
  events: Event[] // added Event interface
  countryLinks: CountryLink[] // updated CountryLink interface
}

interface Address {
  city: string | null
  country: {
    code: string
    name: string
  }
  postCode: string | null
  region: string
  street: string | null
}

interface Event {
  type: string
  address: Address | null
  fullDate: string
  year: number
  day: number | null
  month: number | null
}

interface CountryLink {
  countryText: string
  type: string
}

export interface ScreeningResultsDto {
  id: string
  caseId: string
  clientId: string
  ongoingScreeningEnabled: boolean
  latestRequiredReviewDate: null | string
  records: ScreeningResultDto[]
  status: NameDto
  systemCaseId: string
  createdAt: string
  type: {
    id: number
    name: string
  }
}

interface SecondaryFieldResult {
  field: { typeId: string; value: string | null }
  typeId: string
  submittedValue: string | null
  matchedValue: string | null
  fieldResult: string
}
export enum WorldCheckScreeningStatus {
  Active = 1,
  Archived = 4,
}

export const isArchivedWorldCheckScreeningStatus = (
  worldCheckScreeningArchivedStatus: number
): boolean => {
  return worldCheckScreeningArchivedStatus === WorldCheckScreeningStatus.Archived
}

export interface WorldCheckScreeningProfile {
  actions: string[]
  active: boolean
  addresses: {
    city: string
    country: {
      code: string
      name: string
    } | null
    postCode: null | string
    region: string
    street: null | string
  }[]
  age: string
  ageAsOfDate: string
  associates: {
    reversed: null
    targetEntityId: string
    targetExternalImportId: string
    type: string
  }[]
  category: string
  comments: null | string
  contacts: string[]
  countryLinks: WorldCheckCountryLinkDto[]
  creationDate: string | null
  deletionDate: string | null
  description: string | null
  details: {
    detailType: string | 'IDENTIFICATION' | 'BIOGRAPHY'
    text: string
    title: string | 'IDENTIFICATION' | 'BIOGRAPHY'
  }[]
  entityId: string
  entityType: string | 'INDIVIDUAL'
  events: WorldCheckEventDto[]
  externalImportId: string
  files: []
  gender: 'MALE' | 'FEMALE'
  identityDocuments: []
  images: []
  isDeceased: null
  lastAdjunctChangeDate: null
  modificationDate: string
  names: {
    fullName: 'Imran RABBANI' | string
    givenName: 'Imran' | string
    languageCode: null
    lastName: 'RABBANI' | string
    originalScript: string
    prefix: null
    suffix: null
    type: 'PRIMARY' | 'LOW_QUALITY_AKA' | string
  }[]
  previousCountryLinks: []
  previousRoles: []
  providerDto: null
  roles: []
  sourceDescription: null
  sourceUris: []
  sources: {
    abbreviation: string
    creationDate: string
    identifier: string
    importIdentifier: null
    name: string
    provider: null
    providerSourceStatus: 'ACTIVE'
    regionOfAuthority: 'UNKNOWN'
    subscriptionCategory: 'STANDARD' | string
    type: {
      categoryDto: null
      identifier: string
      name: string
    }
  }[]
  subCategory: null
  updateCategory: string
  updatedDatesDto: null
  weblinks: {
    caption: null
    tags: string[]
    uri: string
  }[]
}

export enum ScreeningResultKey {
  resultId = 'resultId',
  referenceId = 'referenceId',
  matchStrength = 'matchStrength',
  matchedTerm = 'matchedTerm',
  submittedTerm = 'submittedTerm',
  matchedNameType = 'matchedNameType',
  secondaryFieldResults = 'secondaryFieldResults',
  sources = 'sources',
  categories = 'categories',
  creationDate = 'creationDate',
  modificationDate = 'modificationDate',
  primaryName = 'primaryName',
  events = 'events',
  countryLinks = 'countryLinks',
  identityDocuments = 'identityDocuments',
  category = 'category',
  providerType = 'providerType',
  gender = 'gender',
}

export interface WorldCheckCountryLinkDto {
  countryText: string
  country: {
    code: string
    name: string
  }
  type: 'NATIONALITY' | 'LOCATION' | 'POB'
}

export interface WorldCheckEventDto {
  address: {
    city: string | null
    country: {
      code: string
      name: string
    }
    postCode: null
    region: string
    street: null
  }
  allegedAddresses: []
  day: number
  fullDate: string
  month: number
  type: string
  year: number
}
